import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import { ApplicationWorkerEndorsementDto } from "model/Application";
import type { CompanyWorker } from "model/Company";
import type {
  CompanyWorkerV3Response,
  UpdateEmploymentSalaryV3Request,
  UpdateEmploymentV3Request,
  UpdateWorkerTaxV3Request,
} from "model/Salary";

const getWorkerEndorsements = async (
  companyId: number,
  workerId: number,
): Promise<AxiosResponse<ApplicationWorkerEndorsementDto[]>> => {
  return await axiosRequest.auth.get<ApplicationWorkerEndorsementDto[]>(
    `${API_ROOT}/companies/${companyId}/endorsement/${workerId}`,
  );
};
const createWorkerEndorsement = async (
  companyId: number,
  workerId: number,
  skill: string,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post<ApplicationWorkerEndorsementDto>(
    `${API_ROOT}/companies/${companyId}/endorsement`,
    { companyId: companyId, workerId: workerId, skill: skill },
  );
};
const deleteWorkerEndorsement = async (
  companyId: number,
  workerId: number,
  id: number,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.delete<any>(
    `${API_ROOT}/companies/${companyId}/endorsement`,
    { data: { companyId: companyId, workerId: workerId, id: id } },
  );
};

type V3StaffItem = {
  workerId: number;
  firstName: string | null;
  lastName: string | null;
  externalId: string | null;
};

type V3StaffResponse = {
  items: V3StaffItem[] | null;
  total: number;
};

interface CachedCompanyWorkersResponse {
  response: AxiosResponse<CompanyWorker[]>;
  cachedAt: number;
}

const companyWorkersInFlightRequests = new Map<
  string,
  Promise<AxiosResponse<CompanyWorker[]>>
>();
const companyWorkersRecentResponses = new Map<
  string,
  CachedCompanyWorkersResponse
>();
const COMPANY_WORKERS_RECENT_RESPONSE_TTL_MS = 10_000;
const COMPANY_WORKERS_PAGE_SIZE = 50;

const getCompanyWorkersPage = (
  companyId: number,
  active: boolean,
  page: number,
  pageSize: number,
): Promise<AxiosResponse<V3StaffResponse>> => {
  const status = active ? "Active" : "All";
  return axiosRequest.auth.get<V3StaffResponse>(
    `${API_ROOT}/companies/${companyId}/workers/v3?page=${page}&pageSize=${pageSize}&status=${status}`,
  );
};

const fetchAllCompanyWorkers = async (
  companyId: number,
  active: boolean,
): Promise<AxiosResponse<CompanyWorker[]>> => {
  const accumulated: CompanyWorker[] = [];
  let page = 0;
  let firstResponse: AxiosResponse<V3StaffResponse> | null = null;

  while (true) {
    const res = await getCompanyWorkersPage(
      companyId,
      active,
      page,
      COMPANY_WORKERS_PAGE_SIZE,
    );
    if (firstResponse === null) firstResponse = res;
    const items = res.data.items ?? [];
    for (const item of items) {
      accumulated.push({
        id: item.workerId,
        firstName: item.firstName,
        lastName: item.lastName,
        externalId: item.externalId,
      });
    }
    if (items.length < COMPANY_WORKERS_PAGE_SIZE) break;
    if (accumulated.length >= res.data.total) break;
    page += 1;
  }

  return { ...firstResponse!, data: accumulated };
};

const getCompanyWorkers = async (
  companyId: number,
  active: boolean,
): Promise<AxiosResponse<CompanyWorker[]>> => {
  const requestKey = `${companyId}:${active}`;
  const recentResponse = companyWorkersRecentResponses.get(requestKey);
  if (
    recentResponse &&
    Date.now() - recentResponse.cachedAt <
      COMPANY_WORKERS_RECENT_RESPONSE_TTL_MS
  ) {
    return recentResponse.response;
  }

  const inFlightRequest = companyWorkersInFlightRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = fetchAllCompanyWorkers(companyId, active)
    .then((response) => {
      companyWorkersRecentResponses.set(requestKey, {
        response,
        cachedAt: Date.now(),
      });
      return response;
    })
    .finally(() => {
      companyWorkersInFlightRequests.delete(requestKey);
    });

  companyWorkersInFlightRequests.set(requestKey, request);
  return request;
};

const getCompanyWorker = (
  companyId: number,
  workerId: number,
): Promise<AxiosResponse<CompanyWorkerV3Response>> =>
  axiosRequest.auth.get<CompanyWorkerV3Response>(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3`,
  );

const updateEmployment = (
  companyId: number,
  workerId: number,
  employmentId: number,
  body: UpdateEmploymentV3Request,
): Promise<AxiosResponse<void>> =>
  axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}`,
    body,
  );

const updateEmploymentSalary = (
  companyId: number,
  workerId: number,
  employmentId: number,
  salaryId: number,
  body: UpdateEmploymentSalaryV3Request,
): Promise<AxiosResponse<void>> =>
  axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/employment/${employmentId}/salary/${salaryId}`,
    body,
  );

const updateWorkerTax = (
  companyId: number,
  workerId: number,
  taxId: number,
  body: UpdateWorkerTaxV3Request,
): Promise<AxiosResponse<void>> =>
  axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/workers/${workerId}/v3/tax/${taxId}`,
    body,
  );

export const worker = {
  getWorkerEndorsements,
  createWorkerEndorsement,
  deleteWorkerEndorsement,
  getCompanyWorkers,
  getCompanyWorker,
  updateEmployment,
  updateEmploymentSalary,
  updateWorkerTax,
};
